import type { CompanyHoliday } from '../../../api/companyHolidays';
import { localMidnight, nextAllDayEnd } from './timeOffDateUtils';

export interface TimeOffSpan {
  startLocal: Date;
  endLocal: Date;
  isAllDay: boolean;
}

export interface HolidayInRange {
  date: string;
  name: string;
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseHolidayDate(value: string): string {
  return value.slice(0, 10);
}

export function buildHolidayDateSet(holidays: CompanyHoliday[]): Set<string> {
  return new Set(holidays.map((h) => parseHolidayDate(h.holidayDate)));
}

export function buildHolidayLookup(holidays: CompanyHoliday[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const h of holidays) {
    map.set(parseHolidayDate(h.holidayDate), h.name);
  }
  return map;
}

export function expandTimeOffCalendarDays(
  startLocal: Date,
  endLocal: Date,
  isAllDay: boolean
): string[] {
  if (isAllDay) {
    const start = localMidnight(startLocal);
    const endExclusive = localMidnight(endLocal);
    const days: string[] = [];
    for (let cursor = new Date(start); cursor < endExclusive; cursor.setDate(cursor.getDate() + 1)) {
      days.push(toDateKey(cursor));
    }
    return days;
  }

  const start = localMidnight(startLocal);
  const end = localMidnight(endLocal);
  const days: string[] = [];
  for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    days.push(toDateKey(cursor));
  }
  return days;
}

export function findHolidaysInRange(
  calendarDays: string[],
  holidayLookup: Map<string, string>
): HolidayInRange[] {
  const found: HolidayInRange[] = [];
  for (const day of calendarDays) {
    const name = holidayLookup.get(day);
    if (name) found.push({ date: day, name });
  }
  return found;
}

function dateFromKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function groupContiguousAllDaySpans(dayKeys: string[]): TimeOffSpan[] {
  if (dayKeys.length === 0) return [];

  const spans: TimeOffSpan[] = [];
  let runStart = dayKeys[0];
  let prev = dayKeys[0];

  for (let i = 1; i < dayKeys.length; i++) {
    const current = dayKeys[i];
    const prevDate = dateFromKey(prev);
    const nextExpected = toDateKey(new Date(prevDate.getFullYear(), prevDate.getMonth(), prevDate.getDate() + 1));
    if (current === nextExpected) {
      prev = current;
      continue;
    }

    spans.push(toAllDaySpan(runStart, prev));
    runStart = current;
    prev = current;
  }

  spans.push(toAllDaySpan(runStart, prev));
  return spans;
}

function toAllDaySpan(startKey: string, endInclusiveKey: string): TimeOffSpan {
  const startLocal = localMidnight(dateFromKey(startKey));
  const endDate = dateFromKey(endInclusiveKey);
  const endLocal = nextAllDayEnd(localMidnight(endDate));
  return { startLocal, endLocal, isAllDay: true };
}

export function excludeHolidaysFromTimeOffRange(
  startLocal: Date,
  endLocal: Date,
  isAllDay: boolean,
  holidayLookup: Map<string, string>
): TimeOffSpan[] {
  const calendarDays = expandTimeOffCalendarDays(startLocal, endLocal, isAllDay);
  const nonHolidayDays = calendarDays.filter((day) => !holidayLookup.has(day));
  if (nonHolidayDays.length === 0) return [];

  if (!isAllDay && calendarDays.length === 1 && nonHolidayDays.length === 1) {
    return [{ startLocal: new Date(startLocal), endLocal: new Date(endLocal), isAllDay: false }];
  }

  return groupContiguousAllDaySpans(nonHolidayDays);
}

export function collectHolidaysForDraft(
  startLocal: Date,
  endLocal: Date,
  isAllDay: boolean,
  holidays: CompanyHoliday[]
): HolidayInRange[] {
  const lookup = buildHolidayLookup(holidays);
  const days = expandTimeOffCalendarDays(startLocal, endLocal, isAllDay);
  return findHolidaysInRange(days, lookup);
}
